import React, { useEffect } from 'react';
import DocumentTitle from 'react-document-title';
import { useTranslation } from 'react-i18next';
import TopNavBar from './nav/TopNavBar';
import SubNavBar from './nav/SubNavBar';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from '../actions/index';
import { showLoading, hideLoading } from 'react-redux-loading-bar'
import { connect } from 'react-redux';
import DashboardFilterContainer from './dashboard/DashboardFilterContainer';
import DashboardContainer from './dashboard/DashboardContainer';
import { apiFailure } from '../actions/internal';
import moment from 'moment-timezone';
import { camelizeKeys } from 'humps';
import PropTypes from 'prop-types';

const DATE_FORMAT = 'YYYY-MM-DD';

const mapDispatchToProps = (dispatch) => {
  return {
    onShowLoading: () => {
      dispatch(showLoading())
    },
    onHideLoading: () => {
      dispatch(hideLoading())
    },
    onApiFailure: (error) => {
      dispatch(apiFailure(error));
    },
    onUpdateFilters: (filters) => {
      dispatch({
        type: 'DASHBOARD_FILTER_RECEIVE',
        meta: {},
        payload: filters,
      });
    },
    onUpdateDashboard: (paylaoad) => {
      dispatch({
        type: 'DASHBOARD_UPDATE_RECEIVE',
        meta: {},
        payload: paylaoad,
      });
    },
  }
};

const mapStateToProps = (state) => {
  return {
    data: state.dashboard,
    meta: state.meta.dashboard.periods,
  }
}

const fetchFilters = async (params) => {
  return fetch(Routes.api_dashboard_dashboard_filters_path(params), { headers: authHeaders() })
};

const fetchAccount = async (params) => {
  return fetch(Routes.account_api_dashboard_dashboard_index_path(params), { headers: authHeaders() })
};

const fetchAsq = async (params) => {
  return fetch(Routes.asq_api_dashboard_dashboard_index_path(params), { headers: authHeaders() })
};

const fetchAsqStats = async (params) => {
  return fetch(Routes.asq_stats_api_dashboard_dashboard_index_path(params), { headers: authHeaders() })
};

const fetchEngagment = async (params) => {
  return fetch(Routes.engagement_api_dashboard_dashboard_index_path(params), { headers: authHeaders() })
};

const fetchInteractivity = async (params) => {
  return fetch(Routes.interactivity_api_dashboard_dashboard_index_path(params), { headers: authHeaders() })
};

const fetchFollowup = async (params) => {
  return fetch(Routes.follow_up_api_dashboard_dashboard_index_path(params), { headers: authHeaders() })
};

const DashboardPage = ({ onHideLoading, onShowLoading, onApiFailure, onUpdateFilters, onUpdateDashboard }) => {
  const { t } = useTranslation();
  const end = moment();
  const start = end.clone().subtract(6, 'days');

  const [params, setParams] = React.useState({ start: start.locale('en').format(DATE_FORMAT), end: end.locale('en').format(DATE_FORMAT), tz: moment.tz.guess() });
  const [isLoading, setIsLoading] = React.useState(true);
  // Tracks whether the store holds a full dashboard payload. Separate from
  // isLoading: a failed request ends the loading state but leaves no data, and
  // the cards must keep rendering placeholders rather than dereference an empty
  // store.
  const [hasData, setHasData] = React.useState(false);

  useEffect(() => {
    const fetchAll = async (params) => {
      onShowLoading();
      setIsLoading(true);

      let diff = 'all'
      if (moment(params.end).diff(moment(params.start), 'days') > 0) {
        diff = 'custom'
      }

      if (moment(params.end).diff(moment(params.start), 'days') === 6) {
        diff = 'week'
      }

      if (moment(params.end).diff(moment(params.start), 'days') === 13) {
        diff = 'weeks_2'
      }

      if (moment(params.end).diff(moment(params.start), 'days') === 29) {
        diff = 'month'
      }

      if (moment(params.end).diff(moment(params.start), 'days') === 59) {
        diff = 'months_2'
      }

      if (moment(params.end).diff(moment(params.start), 'days') === 179) {
        diff = 'months_6'
      }

      params.periods = {
        account: diff,
        asq: diff,
        asqstats: diff,
        engagement: diff,
        interactivity: diff,
        followUp: diff,
      }

      try {
        const responses = await Promise.all([
          fetchFilters(params),
          fetchAccount(params),
          fetchAsq(params),
          fetchAsqStats(params),
          fetchEngagment(params),
          fetchInteractivity(params),
          fetchFollowup(params),
        ]);

        const errors = responses.filter((response) => !response.ok);
        if (errors.length > 0) {
          // statusText is always empty over HTTP/2, so fall back to a generic message
          // to avoid showing an empty error toast.
          onApiFailure(errors[0].statusText || t('errors.generic'));
          return;
        }

        const json = responses.map((response) => response.json());
        const data = await Promise.all(json);
        onUpdateFilters(data.shift());

        const camelizedData = data.map(json => {
          return camelizeKeys(json, (key, convert) => /(-)+/.test(key) ? key : convert(key));
        })

        onUpdateDashboard(camelizedData.shift())
        onUpdateDashboard(camelizedData.shift())
        onUpdateDashboard(camelizedData.shift())
        onUpdateDashboard(camelizedData.shift())
        onUpdateDashboard(camelizedData.shift())
        onUpdateDashboard(camelizedData.shift())

        setHasData(true);
      } catch (error) {
        onApiFailure(t('errors.generic'));
      } finally {
        // Always clear the loading state so the filters stay usable and a retry
        // is possible. Whether the cards can render is governed by hasData.
        onHideLoading();
        setIsLoading(false);
      }
    }

    fetchAll(params);
  }, [params, onShowLoading, onUpdateFilters, onHideLoading, onApiFailure, onUpdateDashboard, setIsLoading, setHasData, t]);

  return (
    <DocumentTitle title={t('dashboard.pageTitle')}>
      <div>
        <TopNavBar />
        <SubNavBar />
        <div className="row">
          <div className="column">
            <DashboardFilterContainer
              onAgeChange={(ages) => setParams({ ...params, ages })}
              onCohortChange={(cohorts) => setParams({ ...params, cohorts })}
              onSubgroupChange={(subgroups) => setParams({ ...params, subgroups })}
              onGroupChange={(groups) => setParams({ ...params, groups })}
              onPartnerChange={(partners) => setParams({ ...params, partners })}
              onPeriodChange={(startDate, endDate) => setParams({ ...params, start: startDate, end: endDate })}
              isLoading={isLoading}
            />
            <DashboardContainer isLoading={!hasData} />
          </div>
        </div>
      </div>
    </DocumentTitle>
  );
}

DashboardPage.propTypes = {
  onHideLoading: PropTypes.func.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onApiFailure: PropTypes.func.isRequired,
  onUpdateFilters: PropTypes.func.isRequired,
  onUpdateDashboard: PropTypes.func.isRequired,
}

export default connect(mapStateToProps, mapDispatchToProps)(DashboardPage);
